// filepath: apps/web/src/features/directory/directoryNav.ts
//
// FR-UI-DIR-1 AC-1..AC-10 — navigation manifest extension consumed
// by apps/web/src/features/cabinet/Sidebar.tsx.
//
// The sidebar calls `directoryNavItems(manifest)` and renders them
// as a Directory section iff `manifest.directoryAccess === true`.
// Each item matches a route in App.tsx's `AuthStateRouter`.

export interface DirectoryNavItem {
  readonly id: string;
  readonly href: string;
  readonly labelKey: string;
  readonly fallback: string;
  readonly testId: string;
}

/** Stable order matches the design composition (composition tree
 *  docs/design/FR-UI-DIR-1/, Section: Directory).
 *
 *  ISS-379 (Slice 0 tenant-alive): the Directory section's previous
 *  shape had two dead links — `/employment` and `/qualifications`
 *  (no list page; the routes only accept `/:personId` and fell
 *  through to the cabinet landing when clicked without an id). The
 *  Counterparties entry is added at the END of the list so the
 *  visual order is preserved for the remaining items. */
export const DIRECTORY_NAV_ITEMS: readonly DirectoryNavItem[] = [
  {
    id: "people",
    href: "/people",
    labelKey: "directory.nav.people",
    fallback: "People",
    testId: "directory-nav-people",
  },
  {
    id: "organization",
    href: "/organization",
    labelKey: "directory.nav.organization",
    fallback: "Organization",
    testId: "directory-nav-organization",
  },
  {
    id: "positions",
    href: "/positions",
    labelKey: "directory.nav.positions",
    fallback: "Positions",
    testId: "directory-nav-positions",
  },
  {
    id: "locations",
    href: "/locations",
    labelKey: "directory.nav.locations",
    fallback: "Locations",
    testId: "directory-nav-locations",
  },
  {
    id: "teams",
    href: "/teams",
    labelKey: "directory.nav.teams",
    fallback: "Teams",
    testId: "directory-nav-teams",
  },
  {
    id: "counterparties",
    href: "/admin/counterparties",
    labelKey: "directory.nav.counterparties",
    fallback: "Counterparties",
    testId: "directory-nav-counterparties",
  },
];

/**
 * Caller-supplied subset of the navigation manifest. We accept the
 * minimal shape we need so the function is decoupled from the full
 * `NavManifest` type in `apps/web/src/features/cabinet/types.ts` --
 * which lives outside the directory feature.
 */
export interface DirectoryNavManifestSlice {
  directoryAccess: boolean;
}

/**
 * Filter the directory nav items against the manifest. Items the
 * user has no access to are dropped -- the caller is responsible for
 * deciding whether the section header itself renders.
 */
export function directoryNavItems(
  manifest: DirectoryNavManifestSlice,
): readonly DirectoryNavItem[] {
  if (!manifest.directoryAccess) return [];
  return DIRECTORY_NAV_ITEMS;
}
